import React from 'react';
import { type FileRejection } from 'react-dropzone';
import { DropzoneWrapper } from '@/components/elements/DropzoneWrapper';
import {
	CTAScreen,
	DropImage,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Button } from '@/components/UI';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useUploadImage,
} from '@/hooks';
import {
	AmplitudeService,
	CookiesStorageService,
	GenerateLandingLinkService,
	GoogleService,
} from '@/services';
import {
	benefitsUpscale,
	funnelReviewsUpscale,
	howItWorkUpscale,
	reviewsLanding,
	standsOutUpscale,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import benefit from '@/public/images/upscaler/benefit.webp';
import howItWork from '@/public/images/upscaler/howWork.webp';
import funnelImg1 from '@/public/images/upscaler/image-left.webp';
import funnelImg2 from '@/public/images/upscaler/image-right.webp';
import whyLift from '@/public/images/upscaler/whyLift.webp';
import styles from './UpscalerLanding.module.scss';

interface Props {}
const UpscalerLanding: React.FC<Props> = () => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'image-upscale-v2',
		locale,
		translations?.['image-upscale-v2'],
	);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		locale,
		translations?.['landing-reviews'],
	);

	// States
	const [loading, setLoading] = React.useState(false);

	// Logic
	const uploadImage = useUploadImage();

	// Helpers
	const isLoading = loading || uploadImage.isLoading;

	// Actions
	const handleDrop = (
		acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		if (fileRejections.length > 0) {
			console.warn('Rejected files:', fileRejections);
			return;
		}

		setLoading(true);
		uploadImage.mutate(
			{ file: acceptedFiles[0] },
			{
				onSuccess: async ({ viewUrl }) => {
					CookiesStorageService.set(
						LANDING_COOKIE_STORAGE.IMAGE_FROM_LANDING,
						viewUrl,
					);
					GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
					await AmplitudeService.logEventAsync(AMPLITUDE_EVENTS.MEDIA_ADDED, {
						context: LANDING_AMPLITUDE_EVENTS.IMAGE_UPSCALE_LANDING_V2,
					});
					setLoading(false);

					window.location.href =
						GenerateLandingLinkService.funnelImageUpscaleV2();
				},
				onError: () => {
					setLoading(false);
				},
			},
		);
	};
	const openGalleryEvent = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.MEDIA_GALLERY_SHOWN, {
			context: LANDING_AMPLITUDE_EVENTS.IMAGE_UPSCALE_LANDING_V2,
		});
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.IMAGE_UPSCALE_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.IMAGE_UPSCALE_V2 },
	);

	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.IMAGE_UPSCALE_V2 });

	return (
		<>
			<DropImage
				imageLeft={{
					src: funnelImg1,
					alt: 'upscale preview',
				}}
				imageRight={{
					src: funnelImg2,
					alt: 'upscale preview',
				}}
				t={t}
				isLoading={isLoading}
				dropzoneOptions={{
					disabled: isLoading,
					multiple: false,
					accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
					maxFiles: LANDING_CONSTANTS.MAX_FILES,
					maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
					onDrop: handleDrop,
					onFileDialogOpen: openGalleryEvent,
				}}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				reviewsData={funnelReviewsUpscale}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				image={howItWork}
				data={howItWorkUpscale}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['upscale__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				image={benefit}
				specialListItem
				data={benefitsUpscale}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['upscale__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* standsOut */}
			<ImageTextSection
				title={t('standsOutData.mainTitle')}
				image={whyLift}
				data={standsOutUpscale}
				t={t}
				classes={{
					imageContainer: styles['upscale__standsOut-image-container'],
					list: styles['upscale__standsOut-list'],
				}}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['upscale__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				title_green={t('tryLiftData.greenText')}
				title2={t('tryLiftData.title2')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonText={t('getStarted')}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing_outline'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['upscale__cta-drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
				classes={{
					title: styles['upscale__tryLift-title'],
					subtitleClass: styles['upscale__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default UpscalerLanding;
